import React, { useState } from 'react';
import { Button, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import {
  CloseOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  UndoOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import '../CapacityButtons.less';
import './CapacitySizeList.less';
import {
  formatEstimatedMonthlyCost,
  resolveCostPerIpuUsd,
} from 'utils/ipuCost';
import {
  CapacityMatrixRecord,
  formatMemory,
  OperatorCell,
  SizeSummary,
} from '../capacitySummary';
import { BulkEditTarget } from '../BulkEditDrawer/BulkEditDrawer';

type NoteState = { state: 'valid' | 'warning' | 'critical'; message: string };

type Props = Readonly<{
  summaries: SizeSummary[];
  loading: boolean;
  canEdit: boolean;
  showIpuCost: boolean;
  costConfig: Parameters<typeof resolveCostPerIpuUsd>[0];
  expandedSizes: string[];
  onExpandedSizesChange: (sizes: string[]) => void;
  onCellClick: (record: CapacityMatrixRecord, clusterCode: string) => void;
  onBulkEdit: (target: BulkEditTarget) => void;
  onAddMissing: (record: CapacityMatrixRecord, clusterCodes: string[]) => void;
}>;

function cellState({ cell, differs }: OperatorCell) {
  if (!cell) return 'missing';
  return differs ? 'differs' : 'match';
}

function offeredCells(items: OperatorCell[]) {
  return items.flatMap(({ operator, cell }) =>
    cell ? [{ clusterCode: operator.clusterCode, cell }] : [],
  );
}

function CoverageBar({ summary }: { summary: SizeSummary }) {
  return (
    <span className="capacity-size-list__coverage">
      <span
        className="capacity-size-list__segments"
        role="img"
        aria-label={`Offered on ${summary.offeredCount} of ${summary.operators.length} operators, ${summary.differingCount} differ from baseline`}
      >
        {summary.operators.map(item => (
          <span
            key={item.operator.clusterCode}
            className={`capacity-size-list__segment capacity-size-list__segment--${cellState(item)}`}
          />
        ))}
      </span>
      <span className="capacity-size-list__coverage-text">
        {summary.offeredCount} of {summary.operators.length}
      </span>
    </span>
  );
}

function NoteIcons({ notes }: { notes?: Record<string, NoteState> }) {
  const flagged = Object.entries(notes ?? {}).filter(
    ([, note]) => note?.state === 'warning' || note?.state === 'critical',
  );
  if (flagged.length === 0) return null;
  return (
    <span className="capacity-size-list__notes">
      {flagged.map(([key, note]) => (
        <Tooltip key={key} title={note.message}>
          <span
            className={`capacity-size-list__note capacity-size-list__note--${note.state}`}
            role="img"
            aria-label={`${key}: ${note.message}`}
            tabIndex={0}
          >
            {note.state === 'critical' ? (
              <ExclamationCircleOutlined />
            ) : (
              <WarningOutlined />
            )}
          </span>
        </Tooltip>
      ))}
    </span>
  );
}

function OperatorBreakdown({
  summary,
  canEdit,
  showIpuCost,
  costConfig,
  onCellClick,
  onBulkEdit,
  onAddMissing,
}: Omit<
  Props,
  'summaries' | 'loading' | 'expandedSizes' | 'onExpandedSizesChange'
> & {
  summary: SizeSummary;
}) {
  const [selected, setSelected] = useState<React.Key[]>([]);
  const selectedItems = summary.operators.filter(item =>
    selected.includes(item.operator.clusterCode),
  );
  const selectedOffered = offeredCells(selectedItems);
  const selectedDiffering = offeredCells(
    selectedItems.filter(item => item.differs),
  );
  const selectedMissing = selectedItems.filter(item => !item.cell);

  const columns: ColumnsType<OperatorCell> = [
    {
      title: 'Operator',
      key: 'operator',
      render: (_, { operator }) => (
        <span className="capacity-size-list__mono">{operator.clusterCode}</span>
      ),
    },
    {
      title: 'Region',
      key: 'region',
      render: (_, { operator }) => (
        <span className="capacity-size-list__muted">{operator.region}</span>
      ),
    },
    {
      title: 'Memory req / limit',
      key: 'memory',
      render: (_, { cell, differs }) =>
        cell ? (
          <span className={differs ? 'capacity-size-list__differs' : undefined}>
            {formatMemory(cell)}
          </span>
        ) : (
          <span className="capacity-size-list__muted">—</span>
        ),
    },
    {
      title: 'CPU',
      key: 'cpu',
      render: (_, { cell, differs }) =>
        cell ? (
          <span className={differs ? 'capacity-size-list__differs' : undefined}>
            {cell.cpu} vCPU
          </span>
        ) : (
          <span className="capacity-size-list__muted">—</span>
        ),
    },
    ...(showIpuCost
      ? [
          {
            title: 'IPU',
            key: 'ipu',
            render: (_, { cell, operator }: OperatorCell) =>
              cell ? (
                <span>
                  {Number(Number(cell.ipu).toFixed(2))} ·{' '}
                  {formatEstimatedMonthlyCost(
                    cell.ipu,
                    resolveCostPerIpuUsd(costConfig, operator.region),
                  )}
                </span>
              ) : null,
          },
        ]
      : []),
    {
      title: 'Difference',
      key: 'difference',
      render: (_, item) => {
        const state = cellState(item);
        const label = {
          missing: 'Not offered',
          differs: item.difference,
          match: 'Matches baseline',
        }[state];
        return (
          <Tag
            className={`capacity-size-list__tag capacity-size-list__tag--${state}`}
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Checks',
      key: 'notes',
      render: (_, { cell }) => (
        <NoteIcons
          notes={(cell as { notesObj?: Record<string, NoteState> })?.notesObj}
        />
      ),
    },
    {
      title: <span className="capacity-size-list__sr-only">Actions</span>,
      key: 'action',
      align: 'right',
      render: (_, item) => {
        const { operator, cell, differs } = item;
        if (cell && differs && summary.baseline) {
          return (
            <span className="capacity-size-list__actions">
              <Button
                className="capacity-btn capacity-btn--sm capacity-btn--warning"
                icon={<UndoOutlined aria-hidden="true" />}
                disabled={!canEdit}
                onClick={() =>
                  onBulkEdit({
                    size: summary.size,
                    baseline: summary.baseline,
                    cells: [{ clusterCode: operator.clusterCode, cell }],
                    startFromBaseline: true,
                  })
                }
              >
                Reset
              </Button>
              <Button
                className="capacity-btn capacity-btn--sm capacity-btn--subtle"
                icon={<EditOutlined aria-hidden="true" />}
                onClick={() =>
                  onCellClick(summary.record, operator.clusterCode)
                }
              >
                Edit
              </Button>
            </span>
          );
        }
        return (
          <Button
            className={
              cell
                ? 'capacity-btn capacity-btn--sm capacity-btn--subtle'
                : 'capacity-btn capacity-btn--sm capacity-btn--outline capacity-btn--dashed'
            }
            icon={
              cell ? (
                <EditOutlined aria-hidden="true" />
              ) : (
                <PlusOutlined aria-hidden="true" />
              )
            }
            disabled={!cell && !canEdit}
            onClick={() => onCellClick(summary.record, operator.clusterCode)}
          >
            {cell ? 'Edit' : 'Add'}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="capacity-size-list__breakdown">
      {selected.length > 0 && (
        <div
          className="capacity-size-list__bulk-bar"
          role="region"
          aria-label="Bulk actions"
        >
          <span className="capacity-size-list__bulk-count">
            {selected.length} of {summary.operators.length} selected
          </span>
          <Button
            className="capacity-btn capacity-btn--sm capacity-btn--on-dark-primary"
            icon={<EditOutlined aria-hidden="true" />}
            disabled={!canEdit || selectedOffered.length === 0}
            onClick={() =>
              onBulkEdit({
                size: summary.size,
                baseline: summary.baseline,
                cells: selectedOffered,
              })
            }
          >
            Edit together
          </Button>
          <Button
            className="capacity-btn capacity-btn--sm capacity-btn--on-dark"
            icon={<UndoOutlined aria-hidden="true" />}
            disabled={!canEdit || selectedDiffering.length === 0}
            onClick={() =>
              onBulkEdit({
                size: summary.size,
                baseline: summary.baseline,
                cells: selectedDiffering,
                startFromBaseline: true,
              })
            }
          >
            Reset {selectedDiffering.length || ''} to baseline
          </Button>
          <Button
            className="capacity-btn capacity-btn--sm capacity-btn--on-dark"
            icon={<PlusOutlined aria-hidden="true" />}
            disabled={!canEdit || selectedMissing.length === 0}
            onClick={() =>
              onAddMissing(
                summary.record,
                selectedMissing.map(item => item.operator.clusterCode),
              )
            }
          >
            Add to {selectedMissing.length || ''} missing
          </Button>
          <Button
            className="capacity-btn capacity-btn--sm capacity-btn--on-dark capacity-size-list__bulk-clear"
            icon={<CloseOutlined aria-hidden="true" />}
            aria-label="Clear selection"
            onClick={() => setSelected([])}
          >
            Clear
          </Button>
        </div>
      )}
      <Table<OperatorCell>
        size="small"
        rowKey={item => item.operator.clusterCode}
        dataSource={summary.operators}
        columns={columns}
        pagination={false}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: setSelected,
          columnWidth: 40,
        }}
        rowClassName={item =>
          item.differs ? 'capacity-size-list__operator-row--differs' : ''
        }
        scroll={{ x: 900 }}
      />
    </div>
  );
}

function CapacitySizeList(props: Props) {
  const {
    summaries,
    loading,
    canEdit,
    expandedSizes,
    onExpandedSizesChange,
    onCellClick,
    onBulkEdit,
  } = props;

  const columns: ColumnsType<SizeSummary> = [
    {
      title: 'Size',
      key: 'size',
      width: 320,
      render: (_, summary) => (
        <div className="capacity-size-list__size">
          <span className="capacity-size-list__size-name">
            <span className="capacity-size-list__size-title">
              {summary.size}
            </span>
            {summary.inPortal && (
              <Tooltip title="Offered in Cloud Portal when creating a cluster">
                <Tag className="capacity-size-list__badge capacity-size-list__badge--portal">
                  Portal
                </Tag>
              </Tooltip>
            )}
            {summary.isCustom ? (
              <Tag className="capacity-size-list__badge">Custom</Tag>
            ) : (
              <Tooltip title="Main sizes can’t be deleted">
                <Tag className="capacity-size-list__badge">Standard</Tag>
              </Tooltip>
            )}
          </span>
          {summary.displayName && (
            <span className="capacity-size-list__size-subtitle">
              {summary.displayName}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Memory req / limit',
      key: 'memory',
      width: 170,
      render: (_, { baseline }) => (baseline ? formatMemory(baseline) : '—'),
    },
    {
      title: 'CPU',
      key: 'cpu',
      width: 100,
      render: (_, { baseline }) => (baseline ? `${baseline.cpu} vCPU` : '—'),
    },
    {
      title: 'Operators',
      key: 'coverage',
      width: 240,
      render: (_, summary) => <CoverageBar summary={summary} />,
    },
    {
      title: 'vs baseline',
      key: 'differs',
      width: 190,
      render: (_, { differingCount }) =>
        differingCount > 0 ? (
          <Tag className="capacity-size-list__tag capacity-size-list__tag--differs">
            {differingCount} operator
            {differingCount === 1 ? ' differs' : 's differ'}
          </Tag>
        ) : (
          <Tag className="capacity-size-list__tag capacity-size-list__tag--match">
            All match
          </Tag>
        ),
    },
    {
      title: <span className="capacity-size-list__sr-only">Actions</span>,
      key: 'action',
      width: 110,
      align: 'right',
      render: (_, summary) => {
        const cells = offeredCells(summary.operators);
        return (
          <Button
            className="capacity-btn capacity-btn--sm capacity-btn--subtle"
            icon={<EditOutlined aria-hidden="true" />}
            disabled={cells.length > 1 && !canEdit}
            onClick={event => {
              event.stopPropagation();
              if (cells.length === 1) {
                onCellClick(summary.record, cells[0].clusterCode);
              } else {
                onBulkEdit({
                  size: summary.size,
                  baseline: summary.baseline,
                  cells,
                });
              }
            }}
          >
            {cells.length > 1 ? 'Edit all' : 'Edit'}
          </Button>
        );
      },
    },
  ];

  return (
    <Table<SizeSummary>
      className="capacity-size-list"
      rowKey="size"
      dataSource={summaries}
      columns={columns}
      loading={loading}
      pagination={false}
      sticky
      scroll={{ x: 980 }}
      expandable={{
        expandedRowKeys: expandedSizes,
        onExpandedRowsChange: keys => onExpandedSizesChange(keys as string[]),
        expandRowByClick: true,
        expandedRowRender: summary => (
          <OperatorBreakdown {...props} summary={summary} />
        ),
      }}
      locale={{ emptyText: 'No sizes match this filter' }}
    />
  );
}

export default CapacitySizeList;
